import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Menu,
    MenuItem,
    Paper,
    Slide,
    Snackbar,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { OktaAuth } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import clipboardCopy from 'clipboard-copy';
import _ from 'lodash';
import React, { forwardRef, FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import './AltHome.scss';
import AppRoutes from './AppRoutes';
import Backend, { TaskDetail } from './backend/Backend';
import AntSwitch from './components/AntSwitch';
import CloneProjectDialog from './components/CloneProjectDialog';
import DateSelectionDialog from './components/DateSelectionDialog';
import DeleteProjectDialog from './components/DeleteProjectDialog';
import IconLink from './components/IconLink';
import SiDialog from './components/SiDialog';
import SITooltip from './components/SITooltip';
import TaskDetailAndStatus from './components/TaskDetailAndStatus';
import TemplateJsonEditor from './components/TemplateJsonEditor';
import UpdateScopDialog from './components/UpdateScopDialog';
import DataSourceType from './DataSourceType';
import AquantCancelIcon from './resources/images/aq-cancel.svg';
import { ReactComponent as CsvHexagon } from './resources/images/csv_hexagon.svg';
import { ReactComponent as ThreeDots } from './resources/images/dots.svg';
import { ReactComponent as ExpandIcon } from './resources/images/expand_icon.svg';
import StatusInProgress from './resources/images/loader.gif';
import RefreshButton from './resources/images/reload_btn.svg';
import { ReactComponent as SalesforceHexagon } from './resources/images/salesforce_hexagon.svg';
import { ReactComponent as ScopIcon } from './resources/images/scop_icon.svg';
import { ReactComponent as ServiceMaxHexagon } from './resources/images/servicemax_hexagon.svg';
import { ReactComponent as StatusVectorError } from './resources/images/status_vector_error.svg';
import { ReactComponent as StatusVectorSucess } from './resources/images/status_vector_success.svg';
import { ReactComponent as TestIcon } from './resources/images/test_icon.svg';
import SFDCAppScope from './sfdc/SFDCAppScope';
import { sfdcOAuthCallbackUrl } from './sfdc/pkce';
import configStore, { ConfigStore } from './stores/ConfigStore';
import { Constants } from './stores/Constants';
import { useTelemetry } from './tools/Telemetry';
import Util, { TenantInfo } from './Util';
import { WizardTypes } from './wizard/Create';
import { DataSettingsSteps } from './wizard/DataSettings';
import TimeFrameDates from './wizard/TimeFrameDates';

interface HomeProps extends RouteComponentProps {
    progress: Function;
    configStore: ConfigStore;
    enableScopManageUsers: Boolean;
    setEnableScopManageUsers: Function;
}

const Transition = forwardRef(
    (
        props: TransitionProps & {
            children: React.ReactElement;
        },
        ref: React.Ref<unknown>,
    ) => <Slide direction="up" ref={ref} {...props} />,
);

export type PartialModes = 'Add new data' | 'Replace latest file';

type UpdateDataMenuButtonName = PartialModes | 'Replace full dataset';

const ScopOffConfirmDialog = (props: {
    isOpen: boolean;
    usersAssigned: number;
    config: any;
    progress: Function;
    setScopDialogOpen: Function;
    setScopOff: Function;
}) => {
    const { isOpen, config, progress, setScopDialogOpen, setScopOff, usersAssigned } = props;

    const dialogSetScopOff = async () => {
        setScopDialogOpen(false);
        progress(true);
        await setScopOff(config.rowid);
        progress(false);
    };
    return (
        <SiDialog open={isOpen} onClose={() => setScopDialogOpen(false)}>
            <DialogTitle>
                <Stack className="DialogTitle">
                    <Typography className="DialogTitleText">Are you sure?</Typography>
                    <img
                        src={AquantCancelIcon}
                        onClick={() => {
                            setScopDialogOpen(false);
                        }}
                        alt="cancel icon"
                        className="CancelIcon"
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <Paper className="DialogContentPaper">
                <DialogContent className="ProcessDialogContent">
                    <DialogContentText className="DialogContentTextNormal">
                        You are about to deactivate Service Co-Pilot for
                        <Box fontWeight="500" display="inline">
                            &nbsp;{config.config_name}
                        </Box>
                    </DialogContentText>
                    {usersAssigned > 0 && (
                        <DialogContentText className="DialogContentTextNormal">
                            {usersAssigned} users are still assigned, and will lose their access to the portal.
                        </DialogContentText>
                    )}
                    <DialogContentText className="DialogContentTextBold">
                        Do you still want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScopDialogOpen(false)} disableRipple className="DialogActionBtn">
                        Discard
                    </Button>
                    <Button
                        onClick={() => dialogSetScopOff()}
                        variant="contained"
                        disableRipple
                        className="DialogActionBtnContained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Paper>
        </SiDialog>
    );
};

const ConfigMenu = (props: {
    disabled: boolean;
    progress: Function;
    config: any;
    handleRunAction: Function;
    handleReconnect: Function;
    handleEditAction: Function;
    handleDownloadMasterFile: Function;
    handleDuplicateProject: Function;
    handleDeleteProject: Function;
    loadProcessData: Function;
    configStore: ConfigStore;
    oktaAuth: OktaAuth;
    refreshHistory: Function;
    handleUserManagement: Function;
    currentTenant: TenantInfo;
    handleUpdateScop: Function;
}) => {
    const { t } = useTranslation();
    const {
        config,
        handleRunAction,
        handleReconnect,
        handleEditAction,
        handleDownloadMasterFile,
        handleDuplicateProject,
        handleDeleteProject,
        loadProcessData,
        configStore,
        oktaAuth,
        refreshHistory,
        handleUserManagement,
        currentTenant,
        handleUpdateScop,
    } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [scopOpen, setScopOpen] = useState<boolean>(false);
    const [updateScopOpen, setUpdateScopOpen] = useState<boolean>(false);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (e) => {
        console.log(e);
        if (!e.target.className.includes('keepOpen')) {
            setAnchorEl(null);
        }
    };

    const setScopOff = async (config_id: string) => {
        const task_response = await Backend.setScopOff(config_id, oktaAuth);
        await refreshHistory(config_id);
        return task_response.data;
    };

    const switchScopOn = (config: any) => {
        if (!config.scop_project) {
            loadProcessData(config, true, false, true);
        } else {
            setScopOpen(true);
        }
    };

    const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const templateOutputExists = () =>
        config.environments[0].processed_files &&
        config.environments[0].processed_files.adapter_output &&
        config.environments[0].processed_files.adapter_output.adapter_output_path !== '' &&
        config.environments[0].processed_files.adapter_output.adapter_output_filename !== '';

    const canOnlyInject = () =>
        templateOutputExists() && config.environments[0].processed_files.process_output_path !== '';

    const onUpdateScopClick = (e, config) => {
        if (config.latest_inject?.created_at || config.scop_schemas?.prod_schema?.updated_at) {
            setUpdateScopOpen(true);
            e.stopPropagation();
        } else {
            handleUpdateScop(config.rowid);
        }
    };

    return (
        <Stack className={`ConfigMenu ${open && 'ConfigMenuOpened'}`}>
            <Button
                disabled={props.disabled}
                id="config-menu-button"
                aria-controls={open ? 'config-menu-button' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                className="ConfigMenuButton"
                data-testid={`projects-menu-btn-${config.rowid}`}
            >
                <ThreeDots style={{ minWidth: '28px' }} />
            </Button>
            <Menu
                className="ConfigMenuItem"
                id="config-menu-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'config-menu-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    style: {
                        marginTop: '9px',
                        boxShadow: '0px 2px 10px rgba(49, 63, 82, 0.211593)',
                        borderRadius: '6px',
                    },
                }}
            >
                <MenuItem disabled>DATA SOURCE</MenuItem>
                {config.config_type !== DataSourceType.csv && (
                    <MenuItem
                        data-testid="projects-menu-btn-pull-data"
                        onClick={() => handleRunAction(config, 'Pull data')}
                    >
                        {t('config-list.pull-data')}
                    </MenuItem>
                )}
                <MenuItem onClick={() => handleRunAction(config, 'Refresh data')}>
                    {t('config-list.refresh-data')}
                </MenuItem>
                {config.config_type !== DataSourceType.csv && (
                    <MenuItem data-testid="projects-menu-btn-switch-org" onClick={() => handleReconnect(config.rowid)}>
                        {t('config-list.switch-org')}
                    </MenuItem>
                )}

                <Divider />

                <MenuItem disabled>DATA MANIPULATION</MenuItem>
                <MenuItem
                    onClick={() =>
                        handleEditAction(config, DataSettingsSteps.TemplateEditor, WizardTypes.EditDataSettings)
                    }
                    data-testid="projects-menu-btn-edit-template"
                >
                    {t('config-list.edit-config')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleEditAction(config, DataSettingsSteps.Mapping, WizardTypes.EditDataSettings)}
                    data-testid="projects-menu-btn-chg-config"
                >
                    {t('config-list.edit-configuration')}
                </MenuItem>
                <MenuItem
                    disabled={!templateOutputExists()}
                    onClick={() => loadProcessData(config, true, false)}
                    data-testid="projects-menu-btn-process-data"
                >
                    Process Data
                </MenuItem>
                <MenuItem
                    disabled={!canOnlyInject()}
                    onClick={() => loadProcessData(config, true, false, false, true)}
                    data-testid="projects-menu-btn-process-data"
                >
                    Inject Data
                </MenuItem>
                <Divider />

                <MenuItem disabled>PROJECT MANAGEMENT</MenuItem>
                <MenuItem onClick={() => handleDownloadMasterFile(config)}>Download Production Dataset</MenuItem>
                <MenuItem onClick={() => handleDuplicateProject(config)}>Duplicate Project</MenuItem>
                <MenuItem onClick={() => handleDeleteProject(config)}>Delete Project</MenuItem>
                <Divider />

                <MenuItem disabled>UPDATE PRODUCTION</MenuItem>
                <MenuItem disabled={!config.scop_schemas?.stage_schema} onClick={(e) => onUpdateScopClick(e, config)}>
                    Update SCOP
                </MenuItem>

                <MenuItem className="keepOpen" disabled={!templateOutputExists()} onClick={() => switchScopOn(config)}>
                    <AntSwitch sx={{ marginRight: '10px' }} defaultChecked={config.scop_project} />
                    SERVICE CO-PILOT
                </MenuItem>
                {config.scop_project ? (
                    <MenuItem onClick={() => openInNewTab(Constants.SERVICE_CO_PILOT_URL)}>
                        Access Service Co-Pilot
                    </MenuItem>
                ) : null}
                {config.scop_project ? (
                    <MenuItem onClick={() => handleUserManagement(config, currentTenant)}>Manage Users</MenuItem>
                ) : null}
                <UpdateScopDialog
                    open={updateScopOpen}
                    onClose={() => setUpdateScopOpen(false)}
                    onSubmit={() => {
                        handleUpdateScop(config.rowid);
                    }}
                    lastUpdateDate={config.scop_schemas?.prod_schema?.updated_at}
                    latestInject={config.latest_inject?.created_at}
                />
            </Menu>
            {config.scop_users ? (
                <ScopOffConfirmDialog
                    isOpen={scopOpen}
                    usersAssigned={config.scop_users.length}
                    config={config}
                    progress={props.progress}
                    setScopDialogOpen={setScopOpen}
                    setScopOff={setScopOff}
                />
            ) : null}
        </Stack>
    );
};

const UpdateDataMenu = (props: {
    connectionExpired: boolean;
    disabled: boolean;
    handleRunAction: Function;
    config: any;
    oktaAuth: OktaAuth;
}) => {
    const { connectionExpired, disabled, handleRunAction, config, oktaAuth } = props;
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [clickedBtn, setClickedBtn] = useState<UpdateDataMenuButtonName | null>(null);
    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const history = useHistory();
    const [lastFileUpdatedAt, setLastFileUpdatedAt] = useState<string | null>(null);
    const [lastFileName, setLastFileName] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (clickedBtn === 'Add new data' || clickedBtn === 'Replace latest file') {
            (async function () {
                setLoading(true);

                if (
                    config?.environments[0]?.processed_files?.source_paths?.masters[0] &&
                    config?.environments[0]?.processed_files?.source_paths?.updated_at
                ) {
                    const fileName = config?.environments[0]?.processed_files?.source_paths?.masters[0]
                        .split('/')
                        .pop();
                    setLastFileName(fileName);
                    setLastFileUpdatedAt(config.environments[0].processed_files.source_paths.updated_at);
                } else {
                    // if last uploaded file info not exists on Processed files, get it from S3
                    const files = await Backend.getFilesByFolderType(config.rowid, 'master', oktaAuth);
                    const lastFile = files.sort((a, b) => (a.last_modified_at > b.last_modified_at ? 1 : -1));
                    setLastFileName(lastFile[0].name);
                    setLastFileUpdatedAt(lastFile[0].last_modified_at);
                }
                configStore.loadConfigData(config, oktaAuth, () => setLoading(false), false);
            })();
        }
    }, [clickedBtn, config, oktaAuth]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (config.config_type !== DataSourceType.csv) {
            handleRunAction(config, 'Refresh data');
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuBtnClick = (btnName: UpdateDataMenuButtonName) => {
        setClickedBtn(btnName);
        setPopupOpen(true);
    };

    const renderPopupContent = () => {
        let popupText = <></>;
        switch (clickedBtn) {
            case 'Add new data':
                popupText = (
                    <>
                        <Typography fontWeight="500" gutterBottom>
                            You are about to append new data to the existing dataset.
                        </Typography>
                        <Typography>
                            You won’t be able to edit the latest data uploaded on{' '}
                            {new Date(lastFileUpdatedAt).toLocaleDateString('en-GB')}:
                        </Typography>
                        <Typography fontWeight="500" gutterBottom>
                            {lastFileName}.
                        </Typography>
                        <Typography>
                            Rest assured, the additional data you are about to upload will be editable until the next
                            data refresh.
                        </Typography>
                    </>
                );
                break;
            case 'Replace latest file':
                popupText = (
                    <>
                        <Typography fontWeight="500" gutterBottom>
                            You are about to replace the data of the latest file: {lastFileName}
                        </Typography>
                        <Typography>The new dataset will be composed of:</Typography>
                        <ul style={{ paddingInlineStart: '10px' }}>
                            <li>
                                <Typography paddingLeft="5px">
                                    The current data, <b>excluding</b> the latest file’s information
                                </Typography>
                            </li>
                            <li>
                                <Typography paddingLeft="5px">
                                    The new data, from the file you are about to upload
                                </Typography>
                            </li>
                        </ul>
                    </>
                );
                break;
            case 'Replace full dataset':
                popupText = (
                    <>
                        <Typography fontWeight="500" gutterBottom>
                            You are about to replace the existing dataset.{' '}
                        </Typography>
                        <Typography>The data from the past uploaded files will be fully erased.</Typography>
                    </>
                );
                break;

            default:
                break;
        }

        const handleBtnConfirm = () => {
            switch (clickedBtn) {
                case 'Add new data':
                    history.push({
                        pathname: AppRoutes.edit,
                        state: {
                            expanded: 'import-data',
                            config,
                            wizardType: WizardTypes.CsvPartial,
                            partialMode: clickedBtn,
                        },
                    });
                    break;
                case 'Replace latest file':
                    history.push({
                        pathname: AppRoutes.edit,
                        state: {
                            expanded: 'import-data',
                            config,
                            wizardType: WizardTypes.CsvPartial,
                            partialMode: clickedBtn,
                        },
                    });
                    break;
                case 'Replace full dataset':
                    handleRunAction(config, 'Refresh data');
                    break;

                default:
                    break;
            }
            setPopupOpen(false);
        };

        return (
            <>
                <DialogTitle>
                    <Stack className="DialogTitle">
                        <Typography className="DialogTitleText">Are you sure?</Typography>
                        <img
                            src={AquantCancelIcon}
                            onClick={() => setPopupOpen(false)}
                            alt="cancel icon"
                            className="CancelIcon"
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent classes={{ root: 'DialogContent' }}>{popupText}</DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setPopupOpen(false)}
                        //disabled={loading}
                        disableRipple
                        className="DialogActionBtn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleBtnConfirm()}
                        //disabled={loading}
                        variant="contained"
                        disableRipple
                        className="DialogActionBtnContained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </>
        );
    };

    return (
        <>
            <SITooltip
                title={
                    connectionExpired
                        ? 'The connection has expired. Please reconnect the source to refresh data.'
                        : null
                }
            >
                <Stack>
                    <Button
                        onClick={handleClick}
                        className={`RefreshBtn ${connectionExpired && 'RefreshBtnDisabled'}`}
                        variant="outlined"
                        disabled={connectionExpired || disabled}
                    >
                        {t('config-list.update-data')}
                    </Button>
                </Stack>
            </SITooltip>
            <Menu
                className="ConfigMenuItem"
                id="update-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'update-menu-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    style: {
                        marginTop: '5px',
                        boxShadow: '0px 2px 10px rgba(49, 63, 82, 0.211593)',
                        borderRadius: '6px',
                        minWidth: '166px',
                    },
                }}
            >
                <MenuItem disabled>PARTIAL UPDATE</MenuItem>
                <MenuItem
                    disabled={!config?.environments[0]?.processed_files?.adapter_output?.adapter_output_path}
                    onClick={() => handleMenuBtnClick('Add new data')}
                >
                    Add new data
                </MenuItem>
                <MenuItem
                    disabled={!config?.environments[0]?.processed_files?.prev_adapter_output?.adapter_output_path}
                    onClick={() => handleMenuBtnClick('Replace latest file')}
                >
                    Replace latest file
                </MenuItem>

                <Divider />

                <MenuItem disabled>FULL UPDATE</MenuItem>
                <MenuItem onClick={() => handleMenuBtnClick('Replace full dataset')}>Replace full dataset</MenuItem>
            </Menu>

            <SiDialog open={popupOpen} onClose={() => setPopupOpen(false)}>
                {(clickedBtn === 'Add new data' || clickedBtn === 'Replace latest file') && loading ? (
                    <CircularProgress
                        data-testid="CircularProgress"
                        size={30}
                        sx={{ margin: 20, alignSelf: 'center', justifySelf: 'center' }}
                    />
                ) : (
                    renderPopupContent()
                )}
            </SiDialog>
        </>
    );
};

const ConfigAccordion = (props: {
    progress: Function;
    config: any;
    oktaAuth: OktaAuth;
    handleEditAction: Function;
    handleRunAction: Function;
    handleDownloadResults: Function;
    handleShowTaskDetails: Function;
    handleReconnect: Function;
    handleDownloadMasterFile: Function;
    handleDuplicateProject: Function;
    handleDeleteProject: Function;
    updateConfigStatus: Function;
    loadProcessData: Function;
    handleUserManagement: Function;
    currentTenant: TenantInfo;
    handleUpdateScop: Function;
    updateConfig: Function;
    index: number;
    configStore: ConfigStore;
}) => {
    const {
        config,
        oktaAuth,
        handleRunAction,
        handleDownloadResults,
        handleShowTaskDetails,
        handleReconnect,
        handleEditAction,
        handleDownloadMasterFile,
        updateConfigStatus,
        handleDuplicateProject,
        handleDeleteProject,
        loadProcessData,
        handleUserManagement,
        currentTenant,
        handleUpdateScop,
        updateConfig,
        configStore,
    } = props;
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [all_history, setHistory] = useState<any>([]);
    const [historyLoading, setHistoryLoading] = useState<boolean>(true);
    const [historyLength, setHistoryLength] = useState<number>(5);
    const [show_test_runs, setShowTestRuns] = useState<boolean>(false);
    const [revokeDialogTask, setRevokeDialogTask] = useState<any | null>(null);

    // auto refresh enabled by default
    const enableTimer = useRef<boolean>(!!isOpen);
    const refreshTimer = useRef(null);

    const configStatusColorMap: { [char: string]: string } = {
        Draft: '#999CA1',
        'Extracting Data': '#999CA1',
        Testing: '#999CA1',
        Error: '#ED4D4D',
        Processing: '#7CA4FF',
        Processed: '#2468FF',
        Publishing: '#8FDBAB',
        Published: '#76CA95',
        'Duplicating Project': '#999CA1',
        Warning: '#F4AC48',
    };
    const historyStatusColorMap: { [char: string]: string } = {
        New: '#F4AC48',
        Running: '#F4AC48',
        Done: '#76CA95',
        Error: '#ED4D4D',
        Warning: '#F4AC48',
    };

    const parseDate = (dateStr: string) => {
        if (!dateStr) {
            return t('config-list.no-data');
        }

        const dt = new Date(Date.parse(dateStr));
        return dt.toLocaleDateString();
    };

    const parseFullDate = (dateStr: string) => {
        if (!dateStr) {
            return t('config-list.no-data');
        }

        const dt = new Date(Date.parse(dateStr));
        const today = new Date();

        if (today.toDateString() === dt.toDateString()) {
            return `Today - ${dt.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            })}`;
        }

        return `${dt.toLocaleDateString()} - ${dt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })}`;
    };

    const parseTime = (dateStr: string) => {
        if (!dateStr) {
            return t('config-list.no-data');
        }

        const dt = new Date(Date.parse(dateStr));
        return dt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const toHoursAndMinutes = (totalSeconds: number) => {
        const totalMinutes = Math.floor(totalSeconds / 60);

        const seconds = totalSeconds % 60;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return { h: hours, m: minutes, s: seconds };
    };

    const calcDateDiff = (startDate: string, endDate: string) => {
        if (!startDate || !endDate) {
            return '0s';
        }

        const diffInSeconds = Math.ceil((Date.parse(endDate) - Date.parse(startDate)) / 1000);

        const hms = toHoursAndMinutes(diffInSeconds);

        const hours = hms.h > 0 ? `${hms.h}h` : '';
        const minutes = hms.m > 0 ? `${hms.m}m` : '';
        const seconds = hms.s > 0 ? `${hms.s}s` : '';

        return `${hours} ${minutes} ${seconds}`;
    };

    const compareAndUpdateLastTask = async (newLastTask: any, configId: string) => {
        const oldConfigLastTask = JSON.stringify(configStore.lastTaskPerConfig[configId]);
        const updatedConfigLastTask = JSON.stringify(newLastTask);

        if (oldConfigLastTask !== updatedConfigLastTask && newLastTask.task_status === 'Done') {
            const updatedConfig = await Backend.getConfig(oktaAuth, configId);
            updateConfig(configId, updatedConfig.data);
        }
    };

    const refreshHistory = useCallback(
        async (config_id: string | null) => {
            setHistoryLoading(true);
            if (!config_id) {
                setHistoryLoading(false);
                return;
            }

            await Backend.getConfigHistory(config_id, oktaAuth).then((historyResponse) => {
                if (historyResponse) {
                    if (configStore.lastTaskPerConfig[config_id]) {
                        compareAndUpdateLastTask(historyResponse.data[0], config_id);
                    }
                    configStore.setLastTaskPerConfig(config_id, historyResponse.data[0]);
                }

                Backend.getConfigStatus(oktaAuth, config_id).then((response) => {
                    setHistory(historyResponse.data);
                    updateConfigStatus(config_id, response.data);
                    if (enableTimer.current === true) {
                        clearTimeout(refreshTimer.current);
                        refreshTimer.current = setTimeout(() => {
                            refreshHistory(config_id);
                        }, 1000 * 20);
                    }
                    setHistoryLoading(false);
                });
            });
        },
        [oktaAuth],
    );

    useEffect(() => {
        if (isOpen && enableTimer) {
            refreshHistory(config.rowid);
        }
    }, [config.rowid, isOpen, refreshHistory]);

    useEffect(() => {
        if (!isOpen) {
            clearTimeout(refreshTimer.current);
        } else {
            enableTimer.current = true;
        }
        return () => {
            clearTimeout(refreshTimer.current);
            enableTimer.current = false;
        };
    }, [isOpen]);

    const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded && enableTimer) {
            setIsOpen(true);
            refreshHistory(config.rowid);
        } else {
            setIsOpen(false);
            refreshHistory(null);
        }
    };

    const handleHistoryExpandClick = () => {
        if (historyLength + 5 < getFilteredHistory().length) {
            setHistoryLength(historyLength + 5);
        } else {
            setHistoryLength(getFilteredHistory().length);
        }
    };

    const getUncompletedStepName = (steps: any) => {
        const first_uncompleted = steps?.find((step) => step.progress < step.total);
        return first_uncompleted?.name;
    };

    const getErrorMessage = (historyRow: any) => {
        const task_steps_completed = JSON.parse(historyRow.task_steps_completed);
        const uncompleted_step = getUncompletedStepName(task_steps_completed);
        let result = '';
        if (task_steps_completed && task_steps_completed.length > 0 && uncompleted_step) {
            result += `Error in ${t(`summary.${uncompleted_step}`)}: `;
        }
        result += historyRow.task_results;
        return result;
    };

    const renderTaskMessage = (historyRow: any) => {
        const title = ['Running', 'New'].includes(historyRow.task_status)
            ? t('config-history-list.progress-action')
            : t('config-history-list.details-action');

        return (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <IconButton
                    disableRipple
                    disabled={historyRow.task_status === 'Revoked'}
                    style={{ padding: 0, marginRight: '8px', cursor: 'pointer' }}
                    onClick={(e) => {
                        handleShowTaskDetails(historyRow);
                    }}
                >
                    <Typography color="#2468FF" fontWeight={500} justifySelf="center" className="HoverUnderline">
                        {title}
                    </Typography>
                </IconButton>
            </div>
        );
    };

    const shouldRenderLine = (status: any, index: number) => {
        const isLast = index === all_history.slice(0, historyLength).length - 1;
        const isRunning = status === 'Running';
        const nextIsRunning = !isLast && all_history[index + 1].task_status === 'Running';

        return !isLast && !isRunning && !nextIsRunning;
    };

    const renderLineIcon = (historyRow: any) => {
        if (historyRow.task_status === 'Running') {
            return <img style={{ width: '20px' }} src={StatusInProgress} alt="loader" />;
        }

        if (['SyncTest', 'CsvTemplateTest'].includes(historyRow.task_type)) {
            return <TestIcon style={{ marginLeft: '-5px' }} />;
        }

        if (historyRow.task_status === 'Done') {
            return <StatusVectorSucess style={{ marginLeft: '3px' }} />;
        }

        return <StatusVectorError style={{ marginLeft: '3px' }} />;
    };

    const renderSourceLogo = (config: any) => {
        const connectionActive = config.config_type !== DataSourceType.csv ? config.environments.length > 0 : true;

        const renderSwitch = () => {
            switch (config.config_type) {
                case DataSourceType.csv:
                    return (
                        <Stack direction="row" alignItems="center">
                            <CsvHexagon style={{ marginRight: '12px' }} />
                            <Typography>{t('config-list.files-uploaded')}</Typography>
                        </Stack>
                    );
                case DataSourceType.sfdc:
                    return (
                        <Stack direction="row" alignItems="center">
                            <SalesforceHexagon style={{ marginRight: '12px' }} />
                            {connectionActive ? (
                                <Typography>{t('config-list.auto-connection')}</Typography>
                            ) : (
                                <SITooltip title="The token used for this connection has expired. Please reconnect the source to refresh data.">
                                    <Typography style={{ color: 'red' }}>
                                        {t('config-list.connection-expired')}
                                    </Typography>
                                </SITooltip>
                            )}
                        </Stack>
                    );
                case DataSourceType.svmx:
                    return (
                        <Stack direction="row" alignItems="center">
                            <ServiceMaxHexagon style={{ marginRight: '12px' }} />
                            {connectionActive ? (
                                <Typography>{t('config-list.auto-connection')}</Typography>
                            ) : (
                                <SITooltip title="The token used for this connection has expired. Please reconnect the source to refresh data.">
                                    <Typography style={{ color: 'red' }}>
                                        {t('config-list.connection-expired')}
                                    </Typography>
                                </SITooltip>
                            )}
                        </Stack>
                    );
                default:
                    return <Typography>{config.config_type}</Typography>;
            }
        };

        return (
            <Stack className="ConnectionStack">
                <Breadcrumbs separator="|">
                    <Stack direction="row" marginRight="20px">
                        {renderSwitch()}
                    </Stack>
                    {!connectionActive && (
                        <Button style={{ marginLeft: '20px' }} onClick={() => handleReconnect(config.rowid)}>
                            {t('config-list.reconnect')}
                        </Button>
                    )}
                </Breadcrumbs>
            </Stack>
        );
    };

    const isConfigLoading = (status) => {
        if (['Extracting Data', 'Processing', 'Publishing', 'Testing', 'Duplicating'].includes(status)) {
            return true;
        }
        return false;
    };

    const getFilteredHistory = () => {
        if (show_test_runs) {
            return all_history;
        }
        const isTest = (line) => ['CsvTemplateTest', 'SyncTest'].includes(line.task_type);
        const isLoading = (line) => ['New', 'Running'].includes(line.task_status);

        return all_history.filter((line) => isLoading(line) || !isTest(line));
    };

    useEffect(() => {
        setHistoryLength(5);
    }, [show_test_runs]);

    const getTaskStatus = (historyRow: any) => {
        const status = historyRow.task_status;
        if (status === 'Running') {
            return 'In Progress';
        }
        if (status === 'Done') {
            return 'Success';
        }
        return status;
    };

    const handleTaskRevoke = async () => {
        await Backend.revokeTask(revokeDialogTask.rowid, oktaAuth);
        setRevokeDialogTask(null);
        refreshHistory(config.rowid);
    };

    const disableProjectActions = () => {
        if (
            isConfigLoading(config.status) &&
            all_history &&
            all_history[0] &&
            all_history[0].task_type === 'Duplicate'
        ) {
            return true;
        }
        return false;
    };
    return (
        <Accordion
            key={config.rowid}
            className="AccordionContainer"
            style={{ borderColor: configStatusColorMap[config.status] }}
            onChange={handleChange}
            data-testid={`projectsRow[${props.index}]`}
        >
            <AccordionSummary
                expandIcon={<ExpandIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                className="AccordionSum"
                data-testid={`projectSummery[${props.index}]`}
            >
                <Breadcrumbs separator="|">
                    <Stack direction="row">
                        {isConfigLoading(config.status) && (
                            <img style={{ width: '20px', marginRight: '20px' }} src={StatusInProgress} alt="loader" />
                        )}
                        <Typography className="ConfigStatus" style={{ color: configStatusColorMap[config.status] }}>
                            {config.status}
                            {isConfigLoading(config.status) && '...'}
                        </Typography>
                    </Stack>
                    <Stack direction="row">
                        <SITooltip title={config.rowid}>
                            <Typography className="ConfigName">{config.config_name}</Typography>
                        </SITooltip>
                        {config.scop_project ? (
                            <SITooltip title="Insights Co-Pilot has been enabled for this customer with this set of data.">
                                <ScopIcon style={{ minWidth: '28px' }} />
                            </SITooltip>
                        ) : null}
                    </Stack>
                    <Typography className="ConfigLastUpdate">
                        {t('config-list.last-update')}: {parseDate(config.updated_at)}
                    </Typography>
                    {!isOpen && config.environments[0].processed_files?.processed_data_start_date ? (
                        <Typography className="DataTimeFrame">
                            {t('config-list.data-time-frame')}:{' '}
                            {
                                <span style={{ marginLeft: '5px' }}>
                                    <TimeFrameDates
                                        from={config.environments[0].processed_files.processed_data_start_date}
                                        to={config.environments[0].processed_files.processed_data_end_date}
                                        isShortFormat
                                    />
                                </span>
                            }
                        </Typography>
                    ) : null}
                </Breadcrumbs>
                {renderSourceLogo(config)}
            </AccordionSummary>
            <AccordionDetails data-testid={`projectContent[${props.index}]`} className="AccordionDetails">
                <Stack direction="row" className="HistoryHeader">
                    <Stack direction="row" alignItems="center">
                        <img
                            style={{ marginRight: '12px', cursor: 'pointer' }}
                            src={RefreshButton}
                            onClick={() => {
                                refreshHistory(config.rowid);
                            }}
                            alt="ref-btn"
                        />
                        <Typography style={{ marginRight: '8px' }}>{t('config-list.data-history')}</Typography>
                        <Typography className="HistoryFetchStatus">
                            {' '}
                            -{' '}
                            {historyLoading ? t('config-history-list.fetching') : t('config-history-list.data-updated')}
                        </Typography>
                        <AntSwitch
                            sx={{ marginLeft: '40px' }}
                            defaultChecked
                            onClick={() => {
                                setShowTestRuns((prev) => !prev);
                            }}
                        />
                        <Typography className="TestTasksSwitchLabel">Hide completed test tasks</Typography>
                    </Stack>

                    <Stack direction="row">
                        <UpdateDataMenu
                            connectionExpired={config.environments.length === 0}
                            disabled={disableProjectActions()}
                            config={config}
                            handleRunAction={handleRunAction}
                            oktaAuth={oktaAuth}
                        />
                        <ConfigMenu
                            disabled={disableProjectActions()}
                            progress={props.progress}
                            config={config}
                            configStore={configStore}
                            handleRunAction={handleRunAction}
                            handleReconnect={handleReconnect}
                            handleEditAction={handleEditAction}
                            handleDownloadMasterFile={handleDownloadMasterFile}
                            handleDuplicateProject={handleDuplicateProject}
                            handleDeleteProject={handleDeleteProject}
                            loadProcessData={loadProcessData}
                            oktaAuth={oktaAuth}
                            refreshHistory={refreshHistory}
                            handleUserManagement={handleUserManagement}
                            currentTenant={currentTenant}
                            handleUpdateScop={handleUpdateScop}
                        />
                    </Stack>
                </Stack>
                {config.environments[0].processed_files?.processed_data_start_date ? (
                    <Stack direction="row" className="ConfigTimeframe">
                        <Typography display="flex" className="ConfigTimeframeText" marginRight="7px">
                            Dataset time frame:
                        </Typography>
                        <TimeFrameDates
                            from={config.environments[0].processed_files.processed_data_start_date}
                            to={config.environments[0].processed_files.processed_data_end_date}
                        />
                    </Stack>
                ) : null}
                <Stack direction="row" className="HistoryDetailsList">
                    <Stack className="HistoryDetailsListStack">
                        {historyLoading && all_history.length === 0 ? (
                            <CircularProgress data-testid="CircularProgress" color="primary" />
                        ) : null}
                        {!historyLoading && all_history && getFilteredHistory().length === 0 ? (
                            <Typography>{t('config-list.no-history')}</Typography>
                        ) : null}
                        {all_history && (!historyLoading || (historyLoading && getFilteredHistory().length > 0)) ? (
                            <>
                                {getFilteredHistory()
                                    .slice(0, historyLength)
                                    .map((historyRow, index) => (
                                        <Stack direction="row" className="HistoryDetails" key={historyRow.rowid}>
                                            {shouldRenderLine(historyRow.task_status, index) && (
                                                <Stack
                                                    className="HistoryLine"
                                                    style={{
                                                        marginTop: ['SyncTest', 'CsvTemplateTest'].includes(
                                                            historyRow.task_type,
                                                        )
                                                            ? '85px'
                                                            : '73px',
                                                    }}
                                                />
                                            )}
                                            {renderLineIcon(historyRow)}

                                            <Stack className={historyRow.task_status === 'Revoked' && 'HistoryStriked'}>
                                                <Breadcrumbs separator="|" className="HistoryBreadcrumbs">
                                                    <Typography>{parseFullDate(historyRow.created_at)}</Typography>
                                                    <Typography fontWeight={500}>{historyRow.description}</Typography>
                                                    <Typography
                                                        fontWeight={500}
                                                        color={historyStatusColorMap[historyRow.task_status]}
                                                    >
                                                        {getTaskStatus(historyRow)}
                                                    </Typography>
                                                    {historyRow.task_status === 'Done' && (
                                                        <Typography>
                                                            Completed at {parseTime(historyRow.task_completed_at)}
                                                        </Typography>
                                                    )}
                                                    {historyRow.task_status === 'Error' && (
                                                        <Typography>
                                                            Stopped at {parseTime(historyRow.task_completed_at)}
                                                        </Typography>
                                                    )}
                                                    <Typography>
                                                        {calcDateDiff(
                                                            historyRow.created_at,
                                                            historyRow.task_completed_at,
                                                        )}
                                                    </Typography>
                                                    {historyRow.task_status === 'Error' && (
                                                        <SITooltip title={getErrorMessage(historyRow)}>
                                                            <div style={{ maxWidth: '250px' }}>
                                                                <Typography
                                                                    sx={{
                                                                        textOverflow: 'ellipsis',
                                                                        overflow: 'hidden',
                                                                        lineClamp: 2,
                                                                        display: '-webkit-box !important',
                                                                        WebkitBoxOrient: 'vertical',
                                                                        WebkitLineClamp: 2,
                                                                        whiteSpace: 'normal',
                                                                    }}
                                                                >
                                                                    {getErrorMessage(historyRow)}
                                                                </Typography>
                                                            </div>
                                                        </SITooltip>
                                                    )}
                                                    {historyRow.task_type !== 'ScopUpdate' &&
                                                        renderTaskMessage(historyRow)}
                                                    {historyRow.task_status === 'Done' &&
                                                        ['SyncTest', 'CsvTemplateTest'].includes(
                                                            historyRow.task_type,
                                                        ) && (
                                                            <IconLink
                                                                disabled={historyRow.task_status === 'Revoked'}
                                                                onClick={() => {
                                                                    handleDownloadResults(historyRow);
                                                                }}
                                                                SvgIcon={undefined}
                                                            >
                                                                {t('common.download-output')}
                                                            </IconLink>
                                                        )}
                                                    {historyRow.revoke && historyRow.task_status !== 'Revoked' ? (
                                                        <Link
                                                            underline="hover"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                            href="#"
                                                            onClick={() => setRevokeDialogTask(historyRow)}
                                                        >
                                                            <Typography fontWeight="500">Revoke</Typography>
                                                        </Link>
                                                    ) : null}
                                                </Breadcrumbs>
                                            </Stack>
                                        </Stack>
                                    ))}
                                {historyLength + 5 < getFilteredHistory().length && (
                                    <Button onClick={handleHistoryExpandClick} className="HistoryLoadBtn">
                                        {t('config-list.more-updates')}
                                    </Button>
                                )}
                            </>
                        ) : null}
                    </Stack>
                </Stack>
            </AccordionDetails>
            {revokeDialogTask ? (
                <SiDialog open={revokeDialogTask !== null} onClose={() => setRevokeDialogTask(null)}>
                    <DialogTitle>
                        <Stack className="DialogTitle">
                            <Typography className="DialogTitleText">Revoke task?</Typography>
                            <img
                                src={AquantCancelIcon}
                                onClick={() => {
                                    setRevokeDialogTask(null);
                                }}
                                alt="cancel icon"
                                className="CancelIcon"
                            />
                        </Stack>
                    </DialogTitle>
                    <Divider />
                    <Paper className="DialogContentPaper">
                        <DialogContent className="ProcessDialogContent">
                            <DialogContentText className="DialogContentTextBold">
                                Do you want to revoke {revokeDialogTask.description} task?
                            </DialogContentText>
                            <DialogContentText gutterBottom className="DialogContentTextNormal">
                                If so, the task will be marked as revoked
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            {/*loading && <CircularProgress sx={{ marginRight: 2 }} size={20} />*/}
                            <Button
                                onClick={() => setRevokeDialogTask(null)}
                                //disabled={loading}
                                disableRipple
                                className="DialogActionBtn"
                            >
                                No
                            </Button>
                            <Button
                                onClick={() => handleTaskRevoke()}
                                //disabled={loading}
                                variant="contained"
                                disableRipple
                                className="DialogActionBtnContained"
                            >
                                Yes
                            </Button>
                        </DialogActions>
                    </Paper>
                </SiDialog>
            ) : null}
        </Accordion>
    );
};

const AltHome: FunctionComponent<HomeProps> = (props: HomeProps) => {
    const history = useHistory();
    const { showProgress } = configStore;
    const { authState, oktaAuth } = useOktaAuth();
    const [all_config, setConfig] = useState<any>([]);
    const [total_configs, setTotal] = useState<number>(0);
    const [openToast, setOpenToast] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<string | null>('');
    const { t } = useTranslation();
    const { Track } = useTelemetry('Home', {});
    const { enableScopManageUsers } = props;
    const { setEnableScopManageUsers } = props;

    const currentTenant =
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo();

    // Reconnect Button:

    const initiateReconnect = (config_id: string) => {
        const win = Util.popupWindow(`/sfdc/source?appScope=${SFDCAppScope.default}`, 'Service Insights - Reconnect');
        (window as any).setLoginResponse = function (
            auth_code: string,
            domain: string,
            _instance_url?: string,
            code_verifier?: string,
        ) {
            (window as any).setLoginResponse = () => { }; // empty it out
            win!.close();
            processReconnect(config_id, auth_code, domain, 'Source', code_verifier);
        };
    };

    async function processReconnect(
        configId: string,
        authCode: string,
        domain: string,
        envType: 'Source' | 'Target',
        code_verifier?: string,
    ) {
        showProgress(true);
        const redirect_uri = sfdcOAuthCallbackUrl(envType === 'Source' ? 'source' : 'target');
        await Backend.reconnect(configId, authCode, domain, envType, oktaAuth, code_verifier, redirect_uri);
        showProgress(false);
        setToastMessage('Reconnected successfully');
    }

    const handleReconnect = (config_id: any) => {
        initiateReconnect(config_id);
    };

    const handleDuplicateProjectDialog = (config: any) => {
        setSelectedConfiguration(config);
        setOpenCloneProjectDialog(true);
    };

    const handleDeleteProjectDialog = async (config: any) => {
        setSelectedConfiguration(config);
        setOpenDeleteProjectDialog(true);
    };

    const handleDownloadMasterFile = async (config: any) => {
        showProgress(true);
        await Backend.downloadMasterFileUrl(config.rowid, oktaAuth);
        showProgress(false);
    };

    async function handleDownloadResults(row: any) {
        const results_id: string = row.task_results;
        const status: string = row.task_status;

        if (status === 'Error') {
            setSelectedTask(row);
            configStore.setNewTask(row.rowid);
            setOpenTaskDetails(true);
        } else {
            showProgress(true);
            await Backend.downloadResultsViaUrl(results_id, oktaAuth);
            showProgress(false);
        }
    }

    const handleUpdateScop = async (configId) => {
        await Backend.updateScop(configId, oktaAuth);
    };

    const [openTemplateJsonEditor, setOpenTemplateJsonEditor] = useState<boolean>(false);
    const [openTaskDetails, setOpenTaskDetails] = useState<boolean>(false);
    const [selectedTask, setSelectedTask] = useState<TaskDetail>(null);
    const [openUploadCsv, setOpenUploadCsv] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState('');
    const { openDateSelection, setOpenDateSelection } = configStore;
    const [openPullDataDateSelection, setOpenPullDataDateSelection] = useState<boolean>(false);
    const [openRefreshDataDateSelection, setOpenRefreshDataDateSelection] = useState<boolean>(false);
    const [selectedConfiguration, setSelectedConfiguration] = useState<any>(null);
    const [configsLoaded, setConfigsLoaded] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [openCloneProjectDialog, setOpenCloneProjectDialog] = useState<boolean>(false);
    const [openDeleteProjectDialog, setOpenDeleteProjectDialog] = useState<boolean>(false);

    async function handleShowTaskDetails(row: any) {
        const config = all_config.filter((item) => item.rowid === row.task_config)[0];

        showProgress(true);
        configStore.loadConfigData(config, oktaAuth, () => showProgress(false), false);

        setSelectedConfiguration(config);
        setSelectedTask(row);
        configStore.setNewTask(row.rowid);
        if (config.config_type !== DataSourceType.csv) {
            const task_data = JSON.parse(row.task_data);
            configStore.setStartDate(task_data.start_date);
            configStore.setEndDate(task_data.end_date);
        }

        setOpenTaskDetails(true);
    }

    const handleClickCloseTaskDetails = () => {
        setOpenTaskDetails(false);
        setSelectedConfiguration(null);
        setSelectedTask(null);
        configStore.resetConfigData();
    };

    const handleClickCloseTemplateJsonEditor = () => {
        setOpenTemplateJsonEditor(false);
        setSelectedConfiguration(null);
    };

    const handleClickCloseUploadCsv = () => {
        setOpenUploadCsv(false);
        setSelectedConfiguration(null);
    };

    const handleClickCloseDateSelectionDialog = () => {
        setOpenPullDataDateSelection(false);
        setOpenRefreshDataDateSelection(false);
        setSelectedConfiguration(null);
    };

    function create() {
        configStore.resetConfigData();
        history.push({
            pathname: AppRoutes.create,
            state: {
                wizardType: WizardTypes.Create,
            },
        });
    }

    async function handleRunAction(row: any, action: 'Pull data' | 'Refresh data' | undefined) {
        const configType = row.config_type;
        if (configType === DataSourceType.csv) {
            handleEditAction(row, 'import-data', WizardTypes.Create);
        } else {
            handleRunApiAction(row, action!);
        }
    }

    const handleUserManagement = (config?: any, currentTenant?: TenantInfo) => {
        showProgress(true);
        history.push({
            pathname: AppRoutes.user_management,
            state: { config, currentTenant },
        });
    };

    async function handleRunCsvAction(row: any) {
        setSelectedConfiguration(row);
        history.push({
            pathname: AppRoutes.edit,
            state: {
                config: row,
                activeStep: WizardTypes.Create,
                expanded: 'import-data',
            },
        });
    }

    async function handleRunApiAction(row: any, action: 'Pull data' | 'Refresh data') {
        setSelectedConfiguration(row);
        if (action === 'Pull data') {
            setOpenPullDataDateSelection(true);
        } else {
            // Refresh data
            setOpenRefreshDataDateSelection(true);
        }
    }

    async function handleRunApiSubmit(startDate: Date, endDate: Date, isPartial: boolean = false) {
        const row = selectedConfiguration;

        handleClickCloseDateSelectionDialog();

        showProgress(true);
        const response = await Backend.runConfig(
            row.rowid,
            oktaAuth,
            startDate,
            endDate,
            undefined,
            undefined,
            undefined,
            isPartial,
        );
        showProgress(false);

        setToastMessage('Run request submitted successfully');
        setOpenToast(true);
        const currentTask = {
            rowid: response.data.rowid,
            task_config: row.rowid,
        };
        handleShowTaskDetails(currentTask);
        configStore.setStartDate(startDate);
        configStore.setEndDate(endDate);
    }

    function handleRunCsvSubmit(row: any) {
        setToastMessage('Files submitted successfully');
        setOpenToast(true);
        handleClickCloseUploadCsv();
        handleShowTaskDetails(row);
    }

    async function handleEditAction(row: any, expanded: string, wizardType: string) {
        setSelectedConfiguration(row);

        history.push({
            pathname: AppRoutes.edit,
            state: {
                expanded,
                config: row,
                wizardType,
            },
        });
    }

    function handleCloseToast() {
        setOpenToast(false);
    }

    // configs list first loads here
    useEffect(() => {
        if (!authState) {
            return;
        }

        if (!authState.isAuthenticated) {
            setConfig(null);
        } else {
            Backend.getConfigs(0, oktaAuth).then((config) => {
                setCurrentPage(0);
                setConfig(config.data.configs);
                setTotal(config.data.total);
                setConfigsLoaded(true);

                if (config.data.configs.some((config) => config.scop_project)) {
                    setEnableScopManageUsers(true);
                }
            });
        }
    }, [authState, oktaAuth]); // Update if authState changes

    const handleLoadMoreConfigs = async () => {
        setConfigsLoaded(false);
        await Backend.getConfigs(currentPage + 1, oktaAuth).then((config) => {
            setConfig([...all_config, ...config.data.configs]);
            setTotal(config.data.total);
            setConfigsLoaded(true);
        });
        setCurrentPage((prev) => prev + 1);
    };

    if (!Util.getCurrentTenantInfo() || !Util.getCurrentTenantInfo().id) {
        history.push(AppRoutes.customers);
        return <div />;
    }

    const handleCopyClick = async (text: string) => {
        try {
            await clipboardCopy(text);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    const updateConfig = (configId: string, newConfig) => {
        const configIndex = all_config.findIndex((config) => config.rowid === configId);
        const newConfigList = _.cloneDeep(all_config);

        newConfigList[configIndex] = newConfig;

        setConfig(newConfigList);
    };

    const updateConfigStatus = (config_id, new_status_dates) => {
        const config_index = all_config.findIndex((config) => config.rowid === config_id);
        const newConfigList = [...all_config];

        if (new_status_dates.status !== all_config[config_index].status) {
            newConfigList[config_index].status = new_status_dates.status;
        }

        if (new_status_dates.dates && all_config[config_index].config_type !== DataSourceType.csv) {
            newConfigList[config_index].environments[0].processed_files.adapter_output.adapter_output_from = new Date(
                new_status_dates.dates.adapter_output_from,
            );
            newConfigList[config_index].environments[0].processed_files.adapter_output.adapter_output_to = new Date(
                new_status_dates.dates.adapter_output_to,
            );
        }

        if (new_status_dates.scop_project !== all_config[config_index].scop_project) {
            newConfigList[config_index].scop_project = new_status_dates.scop_project;
            if (newConfigList[config_index].scop_project) {
                setEnableScopManageUsers(true);
            }
        }

        if (new_status_dates.scop_users !== all_config[config_index].scop_users) {
            newConfigList[config_index].scop_users = new_status_dates.scop_users;
        }

        if (new_status_dates.delete_project) {
            newConfigList[config_index].is_archived = true;
        }

        // update the configs list with the latest results (in case the user clicked load more button)
        setConfig((prev) => [
            ...newConfigList.filter((config) => !config.is_archived),
            ...prev.filter(
                (obj2) =>
                    !newConfigList.some((obj1) => obj1.rowid === obj2.rowid || obj1.is_archived) && !obj2.is_archived,
            ),
        ]);
    };

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };

    const getFilteredConfigList = () => {
        const orderByDateDesc = (a, b) => {
            const firstDate = new Date(Date.parse(a.created_at));
            const secondDate = new Date(Date.parse(b.created_at));
            if (firstDate < secondDate) {
                return 1;
            }
            if (firstDate > secondDate) {
                return -1;
            }
            return 0;
        };
        if (searchValue.length > 0) {
            const newList = all_config.filter((config) =>
                config.config_name.toLowerCase().includes(searchValue.toLowerCase()),
            );
            return newList.sort(orderByDateDesc);
        }
        return all_config.sort(orderByDateDesc);
    };

    const loadProcessData = async (
        config: any,
        processOnlyMode,
        isPartial,
        processToDB = false,
        injectOnly = false,
    ) => {
        showProgress(true);
        if (config.config_type !== DataSourceType.csv && (processOnlyMode || injectOnly)) {
            // if processOnly - dates are equal to project dates
            configStore.setStartDate(
                new Date(config?.environments[0]?.processed_files?.adapter_output?.adapter_output_from),
            );
            configStore.setEndDate(
                new Date(config?.environments[0]?.processed_files?.adapter_output?.adapter_output_to),
            );
        }
        configStore.loadConfigData(config, oktaAuth, () => {
            showProgress(false);
            configStore.setProcessToDB(processToDB);
            if (injectOnly) {
                configStore.setinjectOnly(true);
                configStore.setProcessDialogOpen(true, undefined, undefined, processOnlyMode, isPartial); // process only is true - no template run here
            } else {
                configStore.setinjectOnly(false);
                configStore.setProcessDialogOpen(true, undefined, undefined, processOnlyMode, isPartial); // process only is true - no template run here
            }
        });
    };

    const handlePullDataDatesDialogSubmit = (startDate, endDate, isPartial = false) => {
        // call pull data
        handleRunApiSubmit(startDate, endDate, isPartial);
    };

    const handleRefreshDataDatesDialogSubmit = (startDate, endDate, isPartial = false) => {
        // call process data dialog
        configStore.setStartDate(startDate);
        configStore.setEndDate(endDate);
        loadProcessData(selectedConfiguration, false, isPartial);
        //configStore.setProcessDialogOpen(true, undefined, undefined, false, isPartial)
        setOpenRefreshDataDateSelection(false);

        //handleRunApiSubmit(startDate, endDate, isPartial)
    };

    const handleDeleteProject = async (config: any) => {
        showProgress(true);
        const tempConfig = { ...config };
        tempConfig.is_archived = true;
        await Backend.updateConfig(tempConfig, oktaAuth);
        updateConfigStatus(config.rowid, { delete_project: true });
        showProgress(false);
        setToastMessage('Project deleted successfully');
        setOpenToast(true);
        setOpenDeleteProjectDialog(false);
    };

    const handleDuplicateProjectSubmit = async (target_tenant_id: string, destination_config_name: string) => {
        setOpenCloneProjectDialog(false);

        props.progress(true);
        const task_response = await Backend.duplicateProject(
            target_tenant_id,
            selectedConfiguration.rowid,
            destination_config_name,
            oktaAuth,
        );
        props.progress(false);

        setToastMessage('Duplicate request submitted successfully');
        setOpenToast(true);

        handleShowTaskDetails(task_response.data);
    };
    return (
        <Track>
            <Stack spacing={2} direction="column" className="Home">
                <Stack className="ConfigsHeader" direction="row">
                    <Breadcrumbs data-testid="projectPageTitle" className="HeaderBreadCrumb">
                        {currentTenant.logo !== 'null' ? (
                            <img src={currentTenant.logo} className="BreadcrumbsLogo" alt="tenant-logo" />
                        ) : (
                            <Typography>{currentTenant.name}</Typography>
                        )}
                        <Typography>{t('config-list.my-projects')}</Typography>
                    </Breadcrumbs>
                    <Button
                        onClick={() => create()}
                        variant="contained"
                        data-testid="projects-add-new-btn"
                        className="NewProjectBtn"
                    >
                        {t('config-list.add-project')}
                    </Button>
                </Stack>

                <Stack className="ConfigsContainer">
                    <Stack className="ConfigsListHeader">
                        <Typography data-testid="listTitleCountProject" className="ListTitle">{`${total_configs} ${total_configs !== 1 ? t('config-list.projects') : t('config-list.project')
                            }`}</Typography>
                        <TextField
                            className="SearchConfigs"
                            inputProps={{
                                'data-testid': 'projects-search-textfield',
                            }}
                            placeholder={t('common.search-by-keyword')}
                            size="small"
                            onChange={handleSearchChange}
                            value={searchValue}
                            // eslint-disable-next-line react/jsx-no-duplicate-props
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" style={{ paddingRight: '20px' }}>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <Stack data-testid="ConfigsListStack" className="ConfigsList">
                        {all_config
                            ? getFilteredConfigList().map((config, index) => (
                                <ConfigAccordion
                                    key={config.rowid}
                                    progress={showProgress}
                                    config={config}
                                    oktaAuth={oktaAuth}
                                    handleEditAction={handleEditAction}
                                    handleRunAction={handleRunAction}
                                    handleDownloadResults={handleDownloadResults}
                                    handleShowTaskDetails={handleShowTaskDetails}
                                    handleReconnect={handleReconnect}
                                    handleDownloadMasterFile={handleDownloadMasterFile}
                                    handleDuplicateProject={handleDuplicateProjectDialog}
                                    handleDeleteProject={handleDeleteProjectDialog}
                                    updateConfigStatus={updateConfigStatus}
                                    loadProcessData={loadProcessData}
                                    handleUserManagement={handleUserManagement}
                                    currentTenant={currentTenant}
                                    handleUpdateScop={handleUpdateScop}
                                    updateConfig={updateConfig}
                                    index={index}
                                    configStore={configStore}
                                />
                            ))
                            : null}
                        {configsLoaded === true &&
                            (getFilteredConfigList().length === 0 || all_config.length === 0) && (
                                <Typography data-testid="configsNotFoundText">
                                    {t('config-list.configs-not-found')}
                                </Typography>
                            )}
                        {configsLoaded === false && (
                            <Stack direction="row" spacing={2}>
                                <CircularProgress data-testid="CircularProgress" size={20} color="primary" />
                                <Typography>{t('config-list.configs-loading')}...</Typography>
                            </Stack>
                        )}
                    </Stack>
                    {configsLoaded && all_config && all_config.length < total_configs ? (
                        <Button
                            data-testid="loadMoreButton"
                            variant="outlined"
                            className="LoadMoreBtn"
                            onClick={() => handleLoadMoreConfigs()}
                        >
                            Load More
                        </Button>
                    ) : null}
                </Stack>
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={openToast}
                    autoHideDuration={3000}
                    onClose={handleCloseToast}
                    message={toastMessage}
                />

                {selectedConfiguration ? (
                    <>
                        <Dialog
                            fullScreen
                            open={openTemplateJsonEditor}
                            onClose={handleClickCloseTemplateJsonEditor}
                            TransitionComponent={Transition}
                        >
                            <AppBar sx={{ position: 'relative' }}>
                                <Toolbar>
                                    <IconButton
                                        edge="start"
                                        color="inherit"
                                        onClick={handleClickCloseTemplateJsonEditor}
                                        aria-label={t('common.close')}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                        {selectedConfiguration.config_name}
                                    </Typography>
                                </Toolbar>
                            </AppBar>
                            <DialogContent classes={{ root: 'DialogContent' }}>
                                <TemplateJsonEditor config={selectedConfiguration} progress={showProgress} />
                            </DialogContent>
                        </Dialog>

                        {/* // pull data dates dialog */}
                        <DateSelectionDialog
                            open={openPullDataDateSelection}
                            onClose={handleClickCloseDateSelectionDialog}
                            onSubmit={(startDate: any, endDate: any, isPartial: boolean = false) => {
                                handlePullDataDatesDialogSubmit(startDate, endDate, isPartial);
                            }}
                            actionTitle="Pull data"
                            actionDescription="data pull"
                            config={selectedConfiguration}
                        />

                        {/* // refresh data dates dialog */}
                        <DateSelectionDialog
                            open={openRefreshDataDateSelection}
                            onClose={handleClickCloseDateSelectionDialog}
                            onSubmit={(startDate: any, endDate: any, isPartial: boolean = false) => {
                                handleRefreshDataDatesDialogSubmit(startDate, endDate, isPartial);
                            }}
                            actionTitle="Refresh data"
                            actionDescription="data refresh"
                            config={selectedConfiguration}
                        />
                        <DeleteProjectDialog
                            onClose={() => setOpenDeleteProjectDialog(false)}
                            open={openDeleteProjectDialog}
                            config={selectedConfiguration}
                            onSubmit={handleDeleteProject}
                        />
                    </>
                ) : null}

                <Dialog
                    fullScreen
                    open={openTaskDetails}
                    onClose={handleClickCloseTaskDetails}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                data-testid="summary-close-button"
                                edge="start"
                                color="inherit"
                                onClick={() => handleClickCloseTaskDetails()}
                                aria-label={t('common.close')}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                {selectedConfiguration ? selectedConfiguration.config_name : null}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent classes={{ root: 'DialogContent' }} style={{ backgroundColor: '#F5F5F5' }}>
                        <Stack direction="column">
                            <Stack direction="row" alignItems="center" spacing={-3}>
                                <Typography className="Title">{t('summary.header')}</Typography>
                                <Tooltip title="Copy Task ID">
                                    <ContentCopyIcon
                                        fontSize="small"
                                        onClick={() => handleCopyClick(configStore.task_id)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': { color: 'blue', verticalAlign: 'bottom' },
                                        }}
                                    />
                                </Tooltip>
                            </Stack>
                            <TaskDetailAndStatus configStore={configStore} task={{ rowid: configStore.task_id }} />
                        </Stack>
                    </DialogContent>
                </Dialog>

                <SiDialog open={openCloneProjectDialog} onClose={() => setOpenCloneProjectDialog(false)}>
                    <CloneProjectDialog
                        handleClose={() => setOpenCloneProjectDialog(false)}
                        handleSubmit={handleDuplicateProjectSubmit}
                        getAvailableTenants={() => Backend.getAllTenants(oktaAuth)}
                    />
                </SiDialog>
            </Stack>
        </Track>
    );
};

export default AltHome;
